import { TEDDIES, teddyPhotoPath, type GameMeta } from '@hazel/shared'

import { pickArtwork } from './artwork.js'
import { useState } from 'react'

export interface TitleCardProps {
  game: GameMeta
  /**
   * Finished artwork, if you'd rather have a designed card than the CSS
   * treatment. Supplying this replaces the framing entirely, so a hand-made
   * card can drop in later with no refactor.
   */
  artworkSrc?: string
}

/** Accent per game, so the four cards read as a set rather than four of a kind. */
const ACCENTS: Record<string, string> = {
  hangman: 'from-coral/25',
  maths: 'from-teal/25',
  memory: 'from-primary/25',
  scramble: 'from-amber/25',
}

/**
 * Finished artwork for the game cards, whatever has actually been dropped in.
 *
 * Asked of the bundler at build time rather than guessed at run time. The first
 * version of this tried `/games/hangman.png`, then `.webp`, then `.jpg`, then
 * `.svg`, falling through on error — which meant four failed requests per card
 * in the ordinary case where no artwork exists yet, and a flicker of broken
 * image on the way past. This knows.
 */
const ARTWORK = import.meta.glob<string>('../assets/games/*.{png,webp,jpg,svg}', {
  eager: true,
  query: '?url',
  import: 'default',
})

/**
 * A game's card: finished artwork if there is any, otherwise the game's title
 * over a photograph of the teddy it is named after.
 *
 * The built-in treatment is CSS over a supplied photo rather than a baked
 * image, so it stays crisp at every size including the TV scale and swapping a
 * photograph is a one-file change. Artwork replaces the whole card, framing and
 * title alike — which is what makes it worth supplying.
 */
export function TitleCard({ game, artworkSrc }: TitleCardProps) {
  const [photoMissing, setPhotoMissing] = useState(false)
  const teddy = TEDDIES[game.teddyKey]

  const artwork = artworkSrc ?? pickArtwork(ARTWORK, game.key)

  if (artwork) {
    return (
      <img
        src={artwork}
        alt={game.title}
        className="aspect-[4/3] w-full rounded-(--radius-control) object-cover"
      />
    )
  }

  return (
    <div className="bg-surface-sunken relative aspect-[4/3] w-full overflow-hidden rounded-(--radius-control)">
      {photoMissing ? (
        // No photograph yet. A large initial reads as deliberate, where a
        // broken image icon reads as broken.
        <div
          className="text-ink-muted/40 font-display absolute inset-0 grid place-items-center text-(length:--text-hero)"
          aria-hidden="true"
        >
          {teddy.name.charAt(0)}
        </div>
      ) : (
        <img
          // The teddy's own photograph, cropped to the card by object-cover.
          // There was a 3:2 drawing for this once; every game has finished
          // artwork now, so it was never reached and has gone.
          src={teddyPhotoPath(game.teddyKey)}
          alt=""
          className="absolute inset-0 size-full object-cover"
          onError={() => setPhotoMissing(true)}
        />
      )}

      {/* Scrim under the title so it stays readable over any photograph. */}
      <div
        className={`absolute inset-0 bg-gradient-to-t to-transparent ${ACCENTS[game.key] ?? 'from-primary/25'}`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

      <h2 className="font-display absolute right-3 bottom-2 left-3 text-(length:--text-heading) leading-tight text-white">
        {game.title}
      </h2>
    </div>
  )
}
